<script setup>
import { createInput } from "@formkit/vue";
import { ArrowTopRightOnSquareIcon, QuestionMarkCircleIcon } from "@heroicons/vue/24/outline";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import ToggleButtons from "./ui/formkit/ToggleButtons.vue";
import IconSelect from "./IconSelect.vue";
import Tooltip from "./ui/Tooltip.vue";
import KnocCardPreview from "./KnocCardPreview.vue";
import TagsInput from "./ui/formkit/TagsInput.vue";

const { t } = useI18n();

const toggleButtons = createInput(ToggleButtons, {
  props: ["options"],
});

const iconSelect = createInput(IconSelect, {
  props: ["options"],
});

const tagsInput = createInput(TagsInput, {
  props: [],
});

const props = defineProps({
  knocOptionsForm: {
    required: true,
  },
  type: {
    type: String,
  },
  isUpdate: {
    type: Boolean,
    default: false,
  },
  oldProtocol: {
    type: String,
  },
  oldAddress: {
    type: String,
  },
  agent: {
    type: Object,
  },
});

const autoComplete = import.meta.env?.VITE_AUTOCOMPLETE || "off";

const agentHaproxySocket = computed(() => props.agent?.Capabilities?.haproxy?.socket_location);

const agentWindowsRules = computed(() => {
  return props.agent?.Capabilities?.windows_firewall?.available_rules
    ? props.agent.Capabilities.windows_firewall.available_rules.sort((a, b) => {
        const getMaxPort = (str) => {
          const ports = str.split(/[-,]/).map(Number);
          return Math.max(...ports);
        };
        return getMaxPort(b.ports) - getMaxPort(a.ports);
      })
    : [];
});

const agentIPv4Sets = computed(() => {
  return props.agent?.Capabilities?.ipset?.ipsets
    ? props.agent.Capabilities.ipset.ipsets
        .filter((ipset) => ipset.address_family === "inet")
        .map((ipset) => ipset.name)
    : [];
});

const agentIPv6Sets = computed(() => {
  return props.agent?.Capabilities?.ipset?.ipsets
    ? props.agent.Capabilities.ipset.ipsets
        .filter((ipset) => ipset.address_family === "inet6")
        .map((ipset) => ipset.name)
    : [];
});

const agentPfTables = computed(() => {
  return props.agent?.Capabilities?.pf?.tables ? props.agent.Capabilities.pf.tables.map((table) => table.name) : [];
});

const linuxBackendType = ref("ipset");
const applicationType = ref("passive");
const vendor = ref(null);

watch(
  applicationType,
  (value) => {
    if (value === "passive+") {
      if (!props.isUpdate) {
        vendor.value = "palo";
      }
      return;
    }

    if (value === "active") {
      if (!props.isUpdate) {
        vendor.value = "fortinet";
      }
    }
  },
  { immediate: true },
);

const proxyType = ref("haproxy");
const scriptType = ref("script");

const protocol = ref("unix");
const address = ref(null);

watch(
  [protocol, props.oldProtocol, props.oldAddress, agentHaproxySocket],
  () => {
    if (protocol.value === "unix") {
      address.value =
        props.isUpdate && props.oldProtocol === "unix" && Boolean(props.oldAddress)
          ? props.oldAddress
          : agentHaproxySocket.value || "/var/run/haproxy.sock";
      return;
    }

    if (protocol.value === "tcp") {
      address.value =
        props.isUpdate && props.oldProtocol === "tcp" && Boolean(props.oldAddress)
          ? props.oldAddress
          : "127.0.0.1:9999";
    }
  },
  { immediate: true },
);

const description = ref("");
const url = ref("");
const icon = ref("fa-sharp fa-solid fa-server");

const form = defineModel();

const cloudflareListIdType = ref(
  (() => {
    if (form.value.BackendConfig?.cloudflarelist?.ListId) {
      return "ListId";
    }
    return "ListName";
  })(),
);

const usingPanorama = ref(!!form.value.ACLConfig?.panos?.SerialNumbers?.length > 0);
const usingPanoramaForAltHost = ref(form.value.BackendConfig?.panos?.MapUserSerialNumbers?.length > 0);

// Initialize linuxBackendType based on existing ACL config (for edit mode)
if (props.isUpdate && form.value?.ACLConfig?.pf) {
  linuxBackendType.value = "pf";
}

watch(usingPanorama, () => {
  if (!!form.value.ACLConfig?.panos?.SerialNumbers?.length > 0) {
    form.value.ACLConfig.panos.SerialNumbers = [];
  }
});
watch(usingPanoramaForAltHost, () => {
  if (form.value.BackendConfig?.panos?.MapUserSerialNumbers?.length > 0) {
    form.value.BackendConfig.panos.MapUserSerialNumbers = [];
  }
});

function isHttpUrl({ value }) {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}
</script>

<template>
  <FormKit v-model="form" type="form" :actions="false">
    <div class="flex flex-col space-y-6">
      <div class="grid grid-cols-2 gap-6">
        <div>
          <div class="space-y-6 rounded-lg border p-6 shadow-sm">
            <div class="flex items-start justify-between">
              <div class="space-y-1.5">
                <h3 class="scroll-m-20 text-2xl font-semibold tracking-tight">
                  {{ t("pages.wizard.steps.settings.heading") }}
                </h3>
              </div>

              <a
                v-if="type === 'WINDOWS'"
                class="inline-flex items-center gap-1 rounded-md border border-transparent bg-secondary px-2.5 py-0.5 text-xs font-semibold text-secondary-foreground transition-colors hover:bg-secondary/80 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                href="https://docs.knocknoc.io/books/admin-guide/page/windows-firewall"
                target="_blank"
                aria-label="Windows firewall documentation (opens in new tab)"
              >
                {{ t("common.docs") }}
                <ArrowTopRightOnSquareIcon class="h-3 w-3" />
                <span class="sr-only">{{ t("common.opensInNewTab") }}</span>
              </a>
              <a
                v-if="type === 'LINUX' && linuxBackendType === 'ipset'"
                class="inline-flex items-center gap-1 rounded-md border border-transparent bg-secondary px-2.5 py-0.5 text-xs font-semibold text-secondary-foreground transition-colors hover:bg-secondary/80 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                href="https://docs.knocknoc.io/books/admin-guide/page/ipset-linux-netfilteriptables"
                target="_blank"
                aria-label="Ipset documentation (opens in new tab)"
              >
                {{ t("common.docs") }}
                <ArrowTopRightOnSquareIcon class="h-3 w-3" />
                <span class="sr-only">{{ t("common.opensInNewTab") }}</span>
              </a>

              <!-- TODO(shh) - Fix this URL -->
              <a
                v-if="type === 'LINUX' && linuxBackendType === 'pf'"
                class="inline-flex items-center gap-1 rounded-md border border-transparent bg-secondary px-2.5 py-0.5 text-xs font-semibold text-secondary-foreground transition-colors hover:bg-secondary/80 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                href="https://docs.knocknoc.io/books/admin-guide/page/pf-packet-filter"
                target="_blank"
                aria-label="PF documentation (opens in new tab)"
              >
                {{ t("common.docs") }}
                <ArrowTopRightOnSquareIcon class="h-3 w-3" />
                <span class="sr-only">{{ t("common.opensInNewTab") }}</span>
              </a>
              <a
                v-else-if="type === 'APPLICATION'"
                class="inline-flex items-center gap-1 rounded-md border border-transparent bg-secondary px-2.5 py-0.5 text-xs font-semibold text-secondary-foreground transition-colors hover:bg-secondary/80 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                href="https://docs.knocknoc.io/books/admin-guide/chapter/knocs-backends"
                target="_blank"
                aria-label="Backend knoc documentation (opens in new tab)"
              >
                {{ t("common.docs") }}
                <ArrowTopRightOnSquareIcon class="h-3 w-3" />
                <span class="sr-only">{{ t("common.opensInNewTab") }}</span>
              </a>
              <a
                v-else-if="type === 'PROXY'"
                class="inline-flex items-center gap-1 rounded-md border border-transparent bg-secondary px-2.5 py-0.5 text-xs font-semibold text-secondary-foreground transition-colors hover:bg-secondary/80 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                href="https://docs.knocknoc.io/books/admin-guide/page/haproxy"
                target="_blank"
                aria-label="Haproxy configuration documentation (opens in new tab)"
              >
                {{ t("common.docs") }}
                <ArrowTopRightOnSquareIcon class="h-3 w-3" />
                <span class="sr-only">{{ t("common.opensInNewTab") }}</span>
              </a>
              <a
                v-else-if="type === 'SCRIPT'"
                class="inline-flex items-center gap-1 rounded-md border border-transparent bg-secondary px-2.5 py-0.5 text-xs font-semibold text-secondary-foreground transition-colors hover:bg-secondary/80 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                href="https://docs.knocknoc.io/books/admin-guide/page/custom-script"
                target="_blank"
                aria-label="Custom script documentation (opens in new tab)"
              >
                {{ t("common.docs") }}
                <ArrowTopRightOnSquareIcon class="h-3 w-3" />
                <span class="sr-only">{{ t("common.opensInNewTab") }}</span>
              </a>
            </div>

            <div class="flex flex-col">
              <div v-if="type === 'APPLICATION'" class="flex items-center">
                <FormKit
                  v-model="applicationType"
                  :classes="{
                    outer: 'grow-0',
                  }"
                  name="ApplicationType"
                  :options="
                    ['passive', 'passive+', 'active'].map((option) => ({
                      label: t(`pages.wizard.steps.settings.fields.ApplicationType.options.${option}`),
                      value: option,
                    }))
                  "
                  :type="toggleButtons"
                />

                <Tooltip
                  aria-label="Passive application type"
                  :focusable="true"
                  :text="t('pages.wizard.steps.settings.fields.ApplicationType.title')"
                  class="-mt-4 ml-3"
                >
                  <QuestionMarkCircleIcon class="h-6 w-6 text-muted-foreground" />
                </Tooltip>
              </div>

              <FormKit
                v-if="type === 'APPLICATION' && applicationType === 'passive+'"
                v-model="vendor"
                :classes="{
                  outer: 'max-w-full',
                }"
                name="Vendor"
                :label="t('pages.wizard.steps.settings.fields.Vendor.passive+.label')"
                :options="
                  ['palo', 'sonicwall'].map((option) => ({
                    label: t(`pages.wizard.steps.settings.fields.Vendor.passive+.options.${option}`),
                    value: option,
                  }))
                "
                type="select"
                validation="required"
              />

              <FormKit
                v-if="type === 'APPLICATION' && applicationType === 'active'"
                v-model="vendor"
                :classes="{
                  outer: 'max-w-full',
                }"
                name="Vendor"
                :label="t('pages.wizard.steps.settings.fields.Vendor.active.label')"
                :options="
                  [
                    'fortinet',
                    'fortimanager',
                    'palo',
                    'firepower',
                    'azure',
                    'sophosutm',
                    'sophossfos',
                    'aws',
                    'sonicwall',
                    'cloudflarelist',
                    'digitalocean',
                  ].map((option) => ({
                    label: t(`pages.wizard.steps.settings.fields.Vendor.active.options.${option}`),
                    value: option,
                  }))
                "
                type="select"
                validation="required"
              />

              <template v-if="type === 'APPLICATION'">
                <template v-if="applicationType === 'passive+'">
                  <template v-if="vendor === 'palo'">
                    <FormKit name="BackendConfig" type="group">
                      <FormKit name="panos" type="group">
                        <FormKit name="usingPano" label="I am using Panorama" type="checkbox" v-model="usingPanorama" />

                        <div class="flex space-x-4">
                          <FormKit
                            :classes="{
                              outer: 'max-w-full',
                            }"
                            name="Hostname"
                            :label="
                              t(
                                `pages.wizard.steps.settings.fields.BackendConfig.fields.panos.fields.Hostname.${usingPanorama ? 'panoLabel' : 'label'}`,
                              )
                            "
                            type="text"
                            validation="required"
                            :autocomplete="autoComplete"
                          />

                          <FormKit
                            :classes="{
                              outer: 'mt-[34px]',
                            }"
                            name="Insecure"
                            :label="
                              t('pages.wizard.steps.settings.fields.BackendConfig.fields.panos.fields.Insecure.label')
                            "
                            type="checkbox"
                          />
                        </div>

                        <FormKit
                          :classes="{
                            outer: 'max-w-full',
                          }"
                          name="ApiKey"
                          :label="
                            t('pages.wizard.steps.settings.fields.BackendConfig.fields.panos.fields.ApiKey.label')
                          "
                          :placeholder="isUpdate ? 'RECORDED' : null"
                          type="text"
                          :validation="isUpdate ? null : 'required'"
                          :autocomplete="autoComplete"
                        />
                      </FormKit>
                    </FormKit>

                    <div class="flex flex-wrap gap-4">
                      <FormKit name="ACLConfig" type="group">
                        <FormKit name="panos" type="group">
                          <FormKit
                            :classes="{
                              outer: 'max-w-full',
                            }"
                            name="EDLName"
                            :label="t('pages.wizard.steps.settings.fields.ACLConfig.fields.panos.fields.EDLName.label')"
                            type="text"
                            validation="required"
                            :autocomplete="autoComplete"
                          />
                        </FormKit>
                      </FormKit>
                      <FormKit name="BackendConfig" type="group">
                        <FormKit name="panos" type="group">
                          <FormKit
                            :classes="{
                              outer: 'max-w-full',
                            }"
                            name="VSys"
                            :label="
                              t('pages.wizard.steps.settings.fields.BackendConfig.fields.panos.fields.VSys.label')
                            "
                            type="text"
                            placeholder="vsys1"
                            :autocomplete="autoComplete"
                          />
                        </FormKit>
                      </FormKit>
                    </div>

                    <FormKit name="ACLConfig" type="group">
                      <FormKit name="panos" type="group">
                        <FormKit
                          :classes="{
                            outer: 'max-w-full',
                          }"
                          name="SerialNumbers"
                          :label="
                            t('pages.wizard.steps.settings.fields.ACLConfig.fields.panos.fields.SerialNumber.label')
                          "
                          :type="tagsInput"
                          :disabled="!usingPanorama"
                          :required="usingPanorama"
                          :validation="usingPanorama ? 'required' : ''"
                          :validation-messages="{
                            required: 'At least one serial number is required',
                          }"
                          help="Firewall serial numbers are usually required for Panorama. Enter one or more."
                        />

                        <FormKit
                          :classes="{
                            outer: 'max-w-full',
                          }"
                          name="MapUser"
                          :label="t('pages.wizard.steps.settings.fields.ACLConfig.fields.panos.fields.MapUser.label')"
                          type="checkbox"
                        />
                      </FormKit>
                    </FormKit>

                    <FormKit name="BackendConfig" type="group">
                      <FormKit name="panos" type="group">
                        <FormKit
                          v-if="form?.ACLConfig?.panos?.MapUser === true"
                          :classes="{
                            outer: 'max-w-full',
                          }"
                          name="MapUserAlternateHost"
                          label="Use an alternative host for user mapping"
                          type="checkbox"
                        />

                        <FormKit
                          v-if="form?.BackendConfig?.panos?.MapUserAlternateHost"
                          name="usingPanoForAltHost"
                          label="The alternative host is Panorama"
                          type="checkbox"
                          v-model="usingPanoramaForAltHost"
                        />

                        <div class="flex space-x-4">
                          <FormKit
                            v-if="form?.BackendConfig?.panos?.MapUserAlternateHost"
                            :classes="{
                              outer: 'max-w-full',
                            }"
                            name="MapUserHostname"
                            :label="
                              t(
                                `pages.wizard.steps.settings.fields.BackendConfig.fields.panos.fields.Hostname.${usingPanoramaForAltHost ? 'altPanoLabel' : 'altLabel'}`,
                              )
                            "
                            type="text"
                            validation="required"
                            :autocomplete="autoComplete"
                          />

                          <FormKit
                            v-if="form?.BackendConfig?.panos?.MapUserAlternateHost"
                            :classes="{
                              outer: 'mt-[34px]',
                            }"
                            name="MapUserInsecure"
                            :label="
                              t('pages.wizard.steps.settings.fields.BackendConfig.fields.panos.fields.Insecure.label')
                            "
                            type="checkbox"
                          />
                        </div>

                        <div class="flex flex-wrap gap-4">
                          <FormKit
                            v-if="form?.BackendConfig?.panos?.MapUserAlternateHost"
                            :classes="{
                              outer: 'max-w-full',
                            }"
                            name="MapUserApiKey"
                            :label="
                              t('pages.wizard.steps.settings.fields.BackendConfig.fields.panos.fields.ApiKey.altLabel')
                            "
                            :placeholder="isUpdate ? 'RECORDED' : null"
                            type="text"
                            :validation="isUpdate ? null : 'required'"
                            :autocomplete="autoComplete"
                          />
                          <FormKit
                            v-if="form?.BackendConfig?.panos?.MapUserAlternateHost"
                            :classes="{
                              outer: 'max-w-full',
                            }"
                            name="MapUserVSys"
                            :label="
                              t('pages.wizard.steps.settings.fields.BackendConfig.fields.panos.fields.VSys.altLabel')
                            "
                            type="text"
                            placeholder="vsys1"
                            :autocomplete="autoComplete"
                          />
                        </div>

                        <FormKit
                          v-if="form?.BackendConfig?.panos?.MapUserAlternateHost"
                          :classes="{
                            outer: 'max-w-full',
                          }"
                          name="MapUserSerialNumbers"
                          :label="
                            t(
                              'pages.wizard.steps.settings.fields.BackendConfig.fields.panos.fields.SerialNumber.altLabel',
                            )
                          "
                          :type="tagsInput"
                          :disabled="!usingPanoramaForAltHost"
                          :required="usingPanorama"
                          :validation="usingPanorama ? 'required' : ''"
                          :validation-messages="{
                            required: 'At least one serial number is required',
                          }"
                          help="Firewall serial numbers are usually required for Panorama. Enter one or more."
                        />
                      </FormKit>
                    </FormKit>
                  </template>
                  <template v-else-if="vendor === 'sonicwall'">
                    <FormKit name="BackendConfig" type="group">
                      <FormKit name="sonicwall" type="group">
                        <div class="flex space-x-4">
                          <FormKit
                            :classes="{
                              outer: 'max-w-full',
                            }"
                            name="Hostname"
                            :label="
                              t(
                                'pages.wizard.steps.settings.fields.BackendConfig.fields.sonicwall.fields.Hostname.label',
                              )
                            "
                            type="text"
                            validation="required"
                            :autocomplete="autoComplete"
                          />

                          <FormKit
                            :classes="{
                              outer: 'mt-[34px]',
                            }"
                            name="Insecure"
                            :label="
                              t(
                                'pages.wizard.steps.settings.fields.BackendConfig.fields.sonicwall.fields.Insecure.label',
                              )
                            "
                            type="checkbox"
                          />
                        </div>
                      </FormKit>
                    </FormKit>

                    <div class="flex space-x-4">
                      <FormKit name="BackendConfig" type="group">
                        <FormKit name="sonicwall" type="group">
                          <FormKit
                            :classes="{
                              outer: 'max-w-full',
                            }"
                            name="Username"
                            :label="
                              t(
                                'pages.wizard.steps.settings.fields.BackendConfig.fields.sonicwall.fields.Username.label',
                              )
                            "
                            type="text"
                            validation="required"
                            :autocomplete="autoComplete"
                          />

                          <FormKit
                            :classes="{
                              outer: 'max-w-full',
                            }"
                            name="Password"
                            :label="
                              t(
                                'pages.wizard.steps.settings.fields.BackendConfig.fields.sonicwall.fields.Password.label',
                              )
                            "
                            :placeholder="isUpdate ? 'RECORDED' : null"
                            type="password"
                            :validation="isUpdate ? null : 'required'"
                          />
                        </FormKit>
                      </FormKit>
                    </div>

                    <FormKit name="ACLConfig" type="group">
                      <FormKit name="sonicwall" type="group">
                        <FormKit
                          :classes="{
                            outer: 'max-w-full',
                          }"
                          name="DynamicGroup"
                          :label="
                            t('pages.wizard.steps.settings.fields.ACLConfig.fields.sonicwall.fields.DynamicGroup.label')
                          "
                          type="text"
                          validation="required"
                          :autocomplete="autoComplete"
                        />
                      </FormKit>
                    </FormKit>
                  </template>
                </template>
                <template v-else-if="applicationType === 'active'">
                  <template v-if="vendor === 'fortinet'">
                    <FormKit name="BackendConfig" type="group">
                      <FormKit name="fortios" type="group">
                        <div class="flex space-x-4">
                          <FormKit
                            :classes="{
                              outer: 'max-w-full',
                            }"
                            name="Url"
                            :label="
                              t('pages.wizard.steps.settings.fields.BackendConfig.fields.fortios.fields.Url.label')
                            "
                            type="text"
                            validation="required|isHttpUrl"
                            :validation-rules="{ isHttpUrl }"
                            :validation-messages="{ isHttpUrl: 'URL must be http or https' }"
                            :autocomplete="autoComplete"
                          />

                          <FormKit
                            :classes="{
                              outer: 'mt-[34px]',
                            }"
                            name="Insecure"
                            :label="
                              t('pages.wizard.steps.settings.fields.BackendConfig.fields.fortios.fields.Insecure.label')
                            "
                            type="checkbox"
                          />
                        </div>

                        <FormKit
                          :classes="{
                            outer: 'max-w-full',
                          }"
                          name="Bearer"
                          :label="
                            t('pages.wizard.steps.settings.fields.BackendConfig.fields.fortios.fields.Bearer.label')
                          "
                          :placeholder="isUpdate ? 'RECORDED' : null"
                          type="text"
                          :validation="isUpdate ? null : 'required'"
                          :autocomplete="autoComplete"
                        />
                      </FormKit>
                    </FormKit>

                    <div class="flex space-x-4">
                      <FormKit name="ACLConfig" type="group">
                        <FormKit name="fortios" type="group">
                          <FormKit
                            :classes="{
                              outer: 'max-w-full',
                            }"
                            name="Name"
                            :label="t('pages.wizard.steps.settings.fields.ACLConfig.fields.fortios.fields.Name.label')"
                            type="text"
                            validation="required"
                            :autocomplete="autoComplete"
                          />
                        </FormKit>
                      </FormKit>

                      <FormKit name="BackendConfig" type="group">
                        <FormKit name="fortios" type="group">
                          <FormKit
                            :classes="{
                              outer: 'max-w-full',
                            }"
                            name="Prefix"
                            :label="
                              t('pages.wizard.steps.settings.fields.BackendConfig.fields.fortios.fields.Prefix.label')
                            "
                            type="text"
                            validation="required"
                            value="kk_"
                            :autocomplete="autoComplete"
                          />
                        </FormKit>
                      </FormKit>
                    </div>

                    <FormKit name="BackendConfig" type="group">
                      <FormKit name="fortios" type="group">
                        <FormKit
                          :classes="{
                            outer: 'max-w-full',
                          }"
                          name="VDOM"
                          :label="
                            t('pages.wizard.steps.settings.fields.BackendConfig.fields.fortios.fields.VDOM.label')
                          "
                          type="text"
                          :autocomplete="autoComplete"
                        />
                      </FormKit>
                    </FormKit>
                  </template>
                  <template v-if="vendor === 'fortimanager'">
                    <div class="flex space-x-4">
                      <FormKit name="BackendConfig" type="group">
                        <FormKit name="fortimanager" type="group">
                          <FormKit
                            :classes="{
                              outer: 'max-w-full',
                            }"
                            name="Hostname"
                            :label="
                              t(
                                'pages.wizard.steps.settings.fields.BackendConfig.fields.fortimanager.fields.Hostname.label',
                              )
                            "
                            type="text"
                            validation="required"
                            :autocomplete="autoComplete"
                          />
                        </FormKit>
                      </FormKit>
                      <FormKit name="BackendConfig" type="group">
                        <FormKit name="fortimanager" type="group">
                          <FormKit
                            :classes="{
                              outer: 'mt-[34px]',
                            }"
                            name="Insecure"
                            :label="
                              t(
                                'pages.wizard.steps.settings.fields.BackendConfig.fields.fortimanager.fields.Insecure.label',
                              )
                            "
                            type="checkbox"
                          />
                        </FormKit>
                      </FormKit>
                    </div>
                    <FormKit name="ACLConfig" type="group">
                      <FormKit name="fortimanager" type="group"> </FormKit>
                    </FormKit>
                    <FormKit name="BackendConfig" type="group">
                      <FormKit name="fortimanager" type="group">
                        <FormKit
                          :classes="{
                            outer: 'max-w-full',
                          }"
                          name="ApiKey"
                          :label="
                            t(
                              'pages.wizard.steps.settings.fields.BackendConfig.fields.fortimanager.fields.ApiKey.label',
                            )
                          "
                          :placeholder="isUpdate ? 'RECORDED' : null"
                          :validation="isUpdate ? null : 'required'"
                          :autocomplete="autoComplete"
                        />
                      </FormKit>
                    </FormKit>
                  </template>
                  <template v-else-if="vendor === 'cloudflarelist'">
                    <FormKit name="BackendConfig" type="group">
                      <FormKit name="cloudflarelist" type="group">
                        <div class="flex flex-wrap gap-x-4">
                          <FormKit
                            :classes="{
                              outer: 'max-w-full',
                            }"
                            name="AccountId"
                            :label="
                              t(
                                'pages.wizard.steps.settings.fields.ACLConfig.fields.cloudflarelist.fields.AccountId.label',
                              )
                            "
                            type="text"
                            validation="required"
                            :autocomplete="autoComplete"
                          />
                          <FormKit
                            :classes="{
                              outer: 'max-w-full',
                            }"
                            name="ApiKey"
                            :label="
                              t(
                                'pages.wizard.steps.settings.fields.ACLConfig.fields.cloudflarelist.fields.ApiKey.label',
                              )
                            "
                            :placeholder="isUpdate ? 'RECORDED' : null"
                            type="text"
                            :validation="isUpdate ? null : 'required'"
                            :autocomplete="autoComplete"
                          />
                        </div>
                        <FormKit
                          v-model="cloudflareListIdType"
                          :classes="{
                            outer: 'grow-0',
                          }"
                          name="ListIdType"
                          :options="
                            ['ListName', 'ListId'].map((option) => ({
                              label: t(
                                `pages.wizard.steps.settings.fields.ACLConfig.fields.cloudflarelist.fields.${option}.label`,
                              ),
                              value: option,
                            }))
                          "
                          :type="toggleButtons"
                        />
                        <FormKit
                          v-if="cloudflareListIdType === 'ListId'"
                          :classes="{
                            outer: 'max-w-full',
                          }"
                          name="ListId"
                          :label="
                            t('pages.wizard.steps.settings.fields.ACLConfig.fields.cloudflarelist.fields.ListId.label')
                          "
                          type="text"
                          validation="required"
                          :autocomplete="autoComplete"
                        />
                        <FormKit
                          v-if="cloudflareListIdType === 'ListName'"
                          :classes="{
                            outer: 'max-w-full',
                          }"
                          name="ListName"
                          :label="
                            t(
                              'pages.wizard.steps.settings.fields.ACLConfig.fields.cloudflarelist.fields.ListName.label',
                            )
                          "
                          type="text"
                          validation="required"
                          :autocomplete="autoComplete"
                        />
                      </FormKit>
                    </FormKit>
                    <FormKit name="ACLConfig" type="group">
                      <FormKit name="cloudflarelist" type="group"> </FormKit>
                    </FormKit>
                  </template>
                  <template v-else-if="vendor === 'digitalocean'">
                    <FormKit name="BackendConfig" type="group">
                      <FormKit name="digitalocean" type="group">
                        <FormKit
                          :classes="{
                            outer: 'max-w-full',
                          }"
                          name="PersonalAccessToken"
                          :label="
                            t(
                              'pages.wizard.steps.settings.fields.BackendConfig.fields.digitalocean.fields.PersonalAccessToken.label',
                            )
                          "
                          :placeholder="isUpdate ? 'RECORDED' : null"
                          type="text"
                          :validation="isUpdate ? null : 'required'"
                          :autocomplete="autoComplete"
                        />
                      </FormKit>
                    </FormKit>

                    <FormKit name="ACLConfig" type="group">
                      <FormKit name="digitalocean" type="group">
                        <FormKit
                          :classes="{
                            outer: 'max-w-full',
                          }"
                          name="FirewallID"
                          :label="
                            t(
                              'pages.wizard.steps.settings.fields.ACLConfig.fields.digitalocean.fields.FirewallID.label',
                            )
                          "
                          type="text"
                          validation="required"
                          :autocomplete="autoComplete"
                        />

                        <FormKit
                          :classes="{
                            outer: 'grow shrink-0 basis-0 max-w-full',
                          }"
                          name="Direction"
                          :label="
                            t('pages.wizard.steps.settings.fields.ACLConfig.fields.digitalocean.fields.Direction.label')
                          "
                          :options="
                            ['inbound', 'outbound'].map((option) => ({
                              label: t(
                                `pages.wizard.steps.settings.fields.ACLConfig.fields.digitalocean.fields.Direction.options.${option}`,
                              ),
                              value: option,
                            }))
                          "
                          type="select"
                          validation="required"
                          :autocomplete="autoComplete"
                        />

                        <div class="flex space-x-4">
                          <FormKit
                            :classes="{
                              outer: 'grow shrink-0 basis-0 max-w-full',
                            }"
                            name="Protocol"
                            :label="
                              t(
                                'pages.wizard.steps.settings.fields.ACLConfig.fields.digitalocean.fields.Protocol.label',
                              )
                            "
                            :options="
                              ['tcp', 'udp', 'icmp'].map((option) => ({
                                label: t(
                                  `pages.wizard.steps.settings.fields.ACLConfig.fields.digitalocean.fields.Protocol.options.${option}`,
                                ),
                                value: option,
                              }))
                            "
                            type="select"
                            validation="required"
                            :autocomplete="autoComplete"
                          />

                          <FormKit
                            :classes="{
                              outer: 'grow shrink-0 basis-0 max-w-full',
                            }"
                            name="Ports"
                            :label="
                              t('pages.wizard.steps.settings.fields.ACLConfig.fields.digitalocean.fields.Ports.label')
                            "
                            type="text"
                            validation="required"
                            :autocomplete="autoComplete"
                          />
                        </div>
                      </FormKit>
                    </FormKit>
                  </template>
                  <template v-else-if="vendor === 'palo'">
                    <FormKit name="BackendConfig" type="group">
                      <FormKit name="panos" type="group">
                        <div class="flex space-x-4">
                          <FormKit
                            :classes="{
                              outer: 'max-w-full',
                            }"
                            name="Hostname"
                            :label="
                              t('pages.wizard.steps.settings.fields.BackendConfig.fields.panos.fields.Hostname.label')
                            "
                            type="text"
                            validation="required"
                            :autocomplete="autoComplete"
                          />

                          <FormKit
                            :classes="{
                              outer: 'mt-[34px]',
                            }"
                            name="Insecure"
                            :label="
                              t('pages.wizard.steps.settings.fields.BackendConfig.fields.panos.fields.Insecure.label')
                            "
                            type="checkbox"
                          />
                        </div>

                        <FormKit
                          :classes="{
                            outer: 'max-w-full',
                          }"
                          name="ApiKey"
                          :label="
                            t('pages.wizard.steps.settings.fields.BackendConfig.fields.panos.fields.ApiKey.label')
                          "
                          :placeholder="isUpdate ? 'RECORDED' : null"
                          type="text"
                          :validation="isUpdate ? null : 'required'"
                          :autocomplete="autoComplete"
                        />
                      </FormKit>
                    </FormKit>

                    <div class="flex space-x-4">
                      <FormKit name="BackendConfig" type="group">
                        <FormKit name="panos" type="group">
                          <FormKit
                            :classes="{
                              outer: 'max-w-full',
                            }"
                            name="VSys"
                            :label="
                              t('pages.wizard.steps.settings.fields.BackendConfig.fields.panos.fields.VSys.label')
                            "
                            type="text"
                            validation="required"
                            placeholder="vsys1"
                            value="vsys1"
                            :autocomplete="autoComplete"
                          />

                          <FormKit
                            :classes="{
                              outer: 'max-w-full',
                            }"
                            name="DeviceGroup"
                            :label="
                              t(
                                'pages.wizard.steps.settings.fields.BackendConfig.fields.panos.fields.DeviceGroup.label',
                              )
                            "
                            type="text"
                            :autocomplete="autoComplete"
                          />
                        </FormKit>
                      </FormKit>
                    </div>

                    <FormKit name="ACLConfig" type="group">
                      <FormKit name="panos" type="group">
                        <FormKit
                          :classes="{
                            outer: 'max-w-full',
                          }"
                          name="GroupName"
                          :label="t('pages.wizard.steps.settings.fields.ACLConfig.fields.panos.fields.GroupName.label')"
                          type="text"
                          validation="required"
                          :autocomplete="autoComplete"
                        />
                      </FormKit>
                    </FormKit>
                  </template>
                  <template v-else-if="vendor === 'firepower'">
                    <FormKit name="BackendConfig" type="group">
                      <FormKit name="firepower" type="group">
                        <div class="flex space-x-4">
                          <FormKit
                            :classes="{
                              outer: 'max-w-full',
                            }"
                            name="Hostname"
                            :label="
                              t(
                                'pages.wizard.steps.settings.fields.BackendConfig.fields.firepower.fields.Hostname.label',
                              )
                            "
                            type="text"
                            validation="required"
                            :autocomplete="autoComplete"
                          />

                          <FormKit
                            :classes="{
                              outer: 'mt-[34px]',
                            }"
                            name="Insecure"
                            :label="
                              t(
                                'pages.wizard.steps.settings.fields.BackendConfig.fields.firepower.fields.Insecure.label',
                              )
                            "
                            type="checkbox"
                          />
                        </div>
                      </FormKit>
                    </FormKit>

                    <div class="flex space-x-4">
                      <FormKit name="BackendConfig" type="group">
                        <FormKit name="firepower" type="group">
                          <FormKit
                            :classes="{
                              outer: 'max-w-full',
                            }"
                            name="Username"
                            :label="
                              t(
                                'pages.wizard.steps.settings.fields.BackendConfig.fields.firepower.fields.Username.label',
                              )
                            "
                            type="text"
                            validation="required"
                            placeholder="username"
                            :autocomplete="autoComplete"
                          />

                          <FormKit
                            :classes="{
                              outer: 'max-w-full',
                            }"
                            name="Password"
                            :label="
                              t(
                                'pages.wizard.steps.settings.fields.BackendConfig.fields.firepower.fields.Password.label',
                              )
                            "
                            :placeholder="isUpdate ? 'RECORDED' : null"
                            type="password"
                            :validation="isUpdate ? null : 'required'"
                          />
                        </FormKit>
                      </FormKit>
                    </div>

                    <FormKit name="ACLConfig" type="group">
                      <FormKit name="firepower" type="group">
                        <FormKit
                          :classes="{
                            outer: 'max-w-full',
                          }"
                          name="GroupUUID"
                          :label="
                            t('pages.wizard.steps.settings.fields.ACLConfig.fields.firepower.fields.GroupUUID.label')
                          "
                          type="text"
                          validation="required"
                          :autocomplete="autoComplete"
                        />
                      </FormKit>
                    </FormKit>
                  </template>
                  <template v-else-if="vendor === 'sonicwall'">
                    <FormKit name="BackendConfig" type="group">
                      <FormKit name="sonicwall" type="group">
                        <div class="flex space-x-4">
                          <FormKit
                            :classes="{
                              outer: 'max-w-full',
                            }"
                            name="Hostname"
                            :label="
                              t(
                                'pages.wizard.steps.settings.fields.BackendConfig.fields.sonicwall.fields.Hostname.label',
                              )
                            "
                            type="text"
                            validation="required"
                            :autocomplete="autoComplete"
                          />

                          <FormKit
                            :classes="{
                              outer: 'mt-[34px]',
                            }"
                            name="Insecure"
                            :label="
                              t(
                                'pages.wizard.steps.settings.fields.BackendConfig.fields.sonicwall.fields.Insecure.label',
                              )
                            "
                            type="checkbox"
                          />
                        </div>
                      </FormKit>
                    </FormKit>

                    <div class="flex space-x-4">
                      <FormKit name="BackendConfig" type="group">
                        <FormKit name="sonicwall" type="group">
                          <FormKit
                            :classes="{
                              outer: 'max-w-full',
                            }"
                            name="Username"
                            :label="
                              t(
                                'pages.wizard.steps.settings.fields.BackendConfig.fields.sonicwall.fields.Username.label',
                              )
                            "
                            type="text"
                            validation="required"
                            :autocomplete="autoComplete"
                          />

                          <FormKit
                            :classes="{
                              outer: 'max-w-full',
                            }"
                            name="Password"
                            :label="
                              t(
                                'pages.wizard.steps.settings.fields.BackendConfig.fields.sonicwall.fields.Password.label',
                              )
                            "
                            :placeholder="isUpdate ? 'RECORDED' : null"
                            type="password"
                            :validation="isUpdate ? null : 'required'"
                          />
                        </FormKit>
                      </FormKit>
                    </div>

                    <FormKit name="ACLConfig" type="group">
                      <FormKit name="sonicwall" type="group">
                        <div class="flex space-x-4">
                          <FormKit
                            :classes="{
                              outer: 'max-w-full',
                            }"
                            name="IPv4Group"
                            :label="
                              t('pages.wizard.steps.settings.fields.ACLConfig.fields.sonicwall.fields.IPv4Group.label')
                            "
                            type="text"
                            validation="required"
                            :autocomplete="autoComplete"
                          />
                          <FormKit
                            :classes="{
                              outer: 'max-w-full',
                            }"
                            name="IPv6Group"
                            :label="
                              t('pages.wizard.steps.settings.fields.ACLConfig.fields.sonicwall.fields.IPv6Group.label')
                            "
                            type="text"
                            validation="required"
                            :autocomplete="autoComplete"
                          />
                        </div>
                      </FormKit>
                    </FormKit>
                  </template>
                  <template v-else-if="vendor === 'sophosutm'">
                    <FormKit name="BackendConfig" type="group">
                      <FormKit name="sophosutm" type="group">
                        <div class="flex space-x-4">
                          <FormKit
                            :classes="{
                              outer: 'max-w-full',
                            }"
                            name="Hostname"
                            :label="
                              t(
                                'pages.wizard.steps.settings.fields.BackendConfig.fields.sophosutm.fields.Hostname.label',
                              )
                            "
                            type="text"
                            validation="required"
                            :autocomplete="autoComplete"
                          />

                          <FormKit
                            :classes="{
                              outer: 'mt-[34px]',
                            }"
                            name="Insecure"
                            :label="
                              t(
                                'pages.wizard.steps.settings.fields.BackendConfig.fields.sophosutm.fields.Insecure.label',
                              )
                            "
                            type="checkbox"
                          />
                        </div>
                      </FormKit>
                    </FormKit>

                    <div class="flex space-x-4">
                      <FormKit name="BackendConfig" type="group">
                        <FormKit name="sophosutm" type="group">
                          <FormKit
                            :classes="{
                              outer: 'max-w-full',
                            }"
                            name="ApiKey"
                            :label="
                              t('pages.wizard.steps.settings.fields.BackendConfig.fields.sophosutm.fields.ApiKey.label')
                            "
                            :placeholder="isUpdate ? 'RECORDED' : null"
                            type="text"
                            :validation="isUpdate ? null : 'required'"
                            :autocomplete="autoComplete"
                          />
                        </FormKit>
                      </FormKit>
                    </div>

                    <FormKit name="ACLConfig" type="group">
                      <FormKit name="sophosutm" type="group">
                        <FormKit
                          :classes="{
                            outer: 'max-w-full',
                          }"
                          name="GroupReference"
                          :label="
                            t(
                              'pages.wizard.steps.settings.fields.ACLConfig.fields.sophosutm.fields.GroupReference.label',
                            )
                          "
                          type="text"
                          validation="required"
                          :autocomplete="autoComplete"
                        />
                      </FormKit>
                    </FormKit>
                  </template>
                  <template v-else-if="vendor === 'sophossfos'">
                    <FormKit name="BackendConfig" type="group">
                      <FormKit name="sophossfos" type="group">
                        <div class="flex space-x-4">
                          <FormKit
                            :classes="{
                              outer: 'max-w-full',
                            }"
                            name="Hostname"
                            :label="
                              t(
                                'pages.wizard.steps.settings.fields.BackendConfig.fields.sophossfos.fields.Hostname.label',
                              )
                            "
                            type="text"
                            validation="required"
                            :autocomplete="autoComplete"
                          />

                          <FormKit
                            :classes="{
                              outer: 'mt-[34px]',
                            }"
                            name="Insecure"
                            :label="
                              t(
                                'pages.wizard.steps.settings.fields.BackendConfig.fields.sophossfos.fields.Insecure.label',
                              )
                            "
                            type="checkbox"
                          />
                        </div>
                      </FormKit>
                    </FormKit>

                    <FormKit name="BackendConfig" type="group">
                      <FormKit name="sophossfos" type="group">
                        <div class="flex space-x-4">
                          <FormKit
                            :classes="{
                              outer: 'max-w-full',
                            }"
                            name="Username"
                            :label="
                              t(
                                'pages.wizard.steps.settings.fields.BackendConfig.fields.sophossfos.fields.Username.label',
                              )
                            "
                            type="text"
                            validation="required"
                            :autocomplete="autoComplete"
                          />
                          <FormKit
                            :classes="{
                              outer: 'max-w-full',
                            }"
                            name="Password"
                            :label="
                              t(
                                'pages.wizard.steps.settings.fields.BackendConfig.fields.sophossfos.fields.Password.label',
                              )
                            "
                            :placeholder="isUpdate ? 'RECORDED' : null"
                            type="password"
                            :validation="isUpdate ? null : 'required'"
                            :autocomplete="autoComplete"
                          />
                        </div>
                      </FormKit>
                    </FormKit>

                    <FormKit name="ACLConfig" type="group">
                      <FormKit name="sophossfos" type="group">
                        <div class="flex space-x-4">
                          <FormKit
                            :classes="{
                              outer: 'max-w-full',
                            }"
                            name="GroupName"
                            :label="
                              t('pages.wizard.steps.settings.fields.ACLConfig.fields.sophossfos.fields.GroupName.label')
                            "
                            type="text"
                            validation="required"
                            :autocomplete="autoComplete"
                          />
                          <FormKit
                            :classes="{
                              outer: 'max-w-full',
                            }"
                            name="GroupNameV6"
                            :label="
                              t(
                                'pages.wizard.steps.settings.fields.ACLConfig.fields.sophossfos.fields.GroupNameV6.label',
                              )
                            "
                            type="text"
                            validation="required"
                            :autocomplete="autoComplete"
                          />
                        </div>
                      </FormKit>
                    </FormKit>
                  </template>
                  <template v-else-if="vendor === 'azure'">
                    <FormKit name="BackendConfig" type="group">
                      <FormKit name="azure" type="group">
                        <FormKit
                          :classes="{
                            outer: 'max-w-full',
                          }"
                          name="ClientId"
                          :label="
                            t('pages.wizard.steps.settings.fields.BackendConfig.fields.azure.fields.ClientId.label')
                          "
                          :placeholder="isUpdate ? 'RECORDED' : null"
                          type="text"
                          :validation="isUpdate ? null : 'required'"
                          :autocomplete="autoComplete"
                        />

                        <FormKit
                          :classes="{
                            outer: 'max-w-full',
                          }"
                          name="ClientSecret"
                          :label="
                            t('pages.wizard.steps.settings.fields.BackendConfig.fields.azure.fields.ClientSecret.label')
                          "
                          :placeholder="isUpdate ? 'RECORDED' : null"
                          type="text"
                          :validation="isUpdate ? null : 'required'"
                          :autocomplete="autoComplete"
                        />

                        <div class="flex space-x-4">
                          <FormKit
                            :classes="{
                              outer: 'max-w-full',
                            }"
                            name="TenantId"
                            :label="
                              t('pages.wizard.steps.settings.fields.BackendConfig.fields.azure.fields.TenantId.label')
                            "
                            :placeholder="isUpdate ? 'RECORDED' : null"
                            type="text"
                            :validation="isUpdate ? null : 'required'"
                            :autocomplete="autoComplete"
                          />

                          <FormKit
                            :classes="{
                              outer: 'mt-[34px]',
                            }"
                            name="NoCache"
                            :label="
                              t('pages.wizard.steps.settings.fields.BackendConfig.fields.azure.fields.NoCache.label')
                            "
                            type="checkbox"
                          />
                        </div>
                      </FormKit>
                    </FormKit>

                    <FormKit name="ACLConfig" type="group">
                      <FormKit name="azure" type="group">
                        <FormKit
                          name="NsgDirection"
                          type="select"
                          :classes="{ outer: 'max-w-full' }"
                          :label="
                            t('pages.wizard.steps.settings.fields.ACLConfig.fields.azure.fields.NsgDirection.label')
                          "
                          :options="[
                            { label: 'Inbound', value: 'Inbound' },
                            { label: 'Outbound', value: 'Outbound' },
                          ]"
                          validation="required"
                        />

                        <div class="flex space-x-4">
                          <FormKit
                            :classes="{
                              outer: 'max-w-full',
                            }"
                            name="NsgName"
                            :label="t('pages.wizard.steps.settings.fields.ACLConfig.fields.azure.fields.NsgName.label')"
                            type="text"
                            validation="required"
                            :autocomplete="autoComplete"
                          />

                          <FormKit
                            :classes="{
                              outer: 'max-w-full',
                            }"
                            name="NsgPort"
                            :label="t('pages.wizard.steps.settings.fields.ACLConfig.fields.azure.fields.NsgPort.label')"
                            type="number"
                            validation="required"
                            value="443"
                            :autocomplete="autoComplete"
                          />
                        </div>

                        <FormKit
                          :classes="{
                            outer: 'max-w-full',
                          }"
                          name="ResourceGroup"
                          :label="
                            t('pages.wizard.steps.settings.fields.ACLConfig.fields.azure.fields.ResourceGroup.label')
                          "
                          type="text"
                          validation="required"
                          :autocomplete="autoComplete"
                        />
                      </FormKit>
                    </FormKit>
                  </template>
                  <template v-else-if="vendor === 'aws'">
                    <FormKit name="BackendConfig" type="group">
                      <FormKit name="aws" type="group">
                        <FormKit
                          :classes="{
                            outer: 'max-w-full',
                          }"
                          name="AccessKeyId"
                          :label="
                            t('pages.wizard.steps.settings.fields.BackendConfig.fields.aws.fields.AccessKeyId.label')
                          "
                          :placeholder="isUpdate ? 'RECORDED' : null"
                          type="text"
                          :validation="isUpdate ? null : 'required'"
                          :autocomplete="autoComplete"
                        />

                        <FormKit
                          :classes="{
                            outer: 'max-w-full',
                          }"
                          name="SecretAccessKey"
                          :label="
                            t(
                              'pages.wizard.steps.settings.fields.BackendConfig.fields.aws.fields.SecretAccessKey.label',
                            )
                          "
                          :placeholder="isUpdate ? 'RECORDED' : null"
                          type="text"
                          :validation="isUpdate ? null : 'required'"
                          :autocomplete="autoComplete"
                        />

                        <FormKit
                          :classes="{
                            outer: 'max-w-full',
                          }"
                          name="Region"
                          :label="t('pages.wizard.steps.settings.fields.BackendConfig.fields.aws.fields.Region.label')"
                          type="text"
                          validation="required"
                          :autocomplete="autoComplete"
                        />
                      </FormKit>
                    </FormKit>

                    <FormKit name="ACLConfig" type="group">
                      <FormKit name="aws" type="group">
                        <div class="flex space-x-4">
                          <FormKit
                            :classes="{
                              outer: 'max-w-full',
                            }"
                            name="SecurityGroupId"
                            :label="
                              t('pages.wizard.steps.settings.fields.ACLConfig.fields.aws.fields.SecurityGroupId.label')
                            "
                            type="text"
                            validation="required"
                            :autocomplete="autoComplete"
                          />

                          <FormKit
                            :classes="{
                              outer: 'max-w-full',
                            }"
                            name="Port"
                            :label="t('pages.wizard.steps.settings.fields.ACLConfig.fields.aws.fields.Port.label')"
                            type="number"
                            validation="required"
                            value="22"
                            :autocomplete="autoComplete"
                          />
                        </div>
                        <div class="flex space-x-4">
                          <FormKit
                            name="Direction"
                            type="select"
                            :classes="{ outer: 'max-w-full' }"
                            :label="t('pages.wizard.steps.settings.fields.ACLConfig.fields.aws.fields.Direction.label')"
                            :options="[
                              { label: 'Inbound', value: 'inbound' },
                              { label: 'Outbound', value: 'outbound' },
                            ]"
                            validation="required"
                          />

                          <FormKit
                            name="Protocol"
                            type="select"
                            :classes="{ outer: 'max-w-full' }"
                            :label="t('pages.wizard.steps.settings.fields.ACLConfig.fields.aws.fields.Protocol.label')"
                            :options="[
                              { label: 'TCP', value: 'tcp' },
                              { label: 'UDP', value: 'udp' },
                            ]"
                            validation="required"
                          />
                        </div>
                      </FormKit>
                    </FormKit>
                  </template>
                </template>
              </template>

              <FormKit
                v-if="type === 'PROXY'"
                v-model="proxyType"
                :classes="{
                  outer: 'max-w-full',
                }"
                name="ProxyType"
                :label="t('pages.wizard.steps.settings.fields.ProxyType.label')"
                :options="
                  ['haproxy', 'knocproxy'].map((option) => ({
                    label: t(`pages.wizard.steps.settings.fields.ProxyType.options.${option}`),
                    value: option,
                  }))
                "
                type="select"
                validation="required"
              />

              <FormKit v-if="type === 'PROXY'" name="ACLConfig" type="group">
                <FormKit v-if="proxyType === 'haproxy'" name="haproxy" type="group">
                  <FormKit
                    :classes="{
                      outer: 'max-w-full',
                    }"
                    name="ID"
                    :label="t('pages.wizard.steps.settings.fields.ACLConfig.haproxy.ID.label')"
                    number
                    :placeholder="t('pages.wizard.steps.settings.fields.ACLConfig.haproxy.ID.placeholder')"
                    type="text"
                    validation="required|number"
                    :autocomplete="autoComplete"
                  />
                </FormKit>

                <FormKit v-if="proxyType === 'knocproxy'" name="knocproxy" type="group">
                  <FormKit
                    :classes="{
                      outer: 'max-w-full',
                    }"
                    name="Name"
                    :label="t('pages.wizard.steps.settings.fields.ACLConfig.knocproxy.Name.label')"
                    :placeholder="t('pages.wizard.steps.settings.fields.ACLConfig.knocproxy.Name.placeholder')"
                    type="text"
                    :validation-messages="{ matches: 'Only letters, numbers, and underscores are allowed.' }"
                    :validation="[['matches', /^[a-zA-Z0-9_]*$/], ['required']]"
                    :autocomplete="autoComplete"
                  />
                </FormKit>
              </FormKit>

              <FormKit v-if="type === 'SCRIPT'" name="BackendConfig" type="group">
                <FormKit name="script" type="group">
                  <template v-if="scriptType === 'script'">
                    <FormKit
                      :classes="{
                        outer: 'max-w-full',
                      }"
                      name="Command"
                      :label="t('pages.wizard.steps.settings.fields.BackendConfig.script.Command.label')"
                      :help="t('pages.wizard.steps.settings.fields.BackendConfig.script.Command.help')"
                      :placeholder="t('pages.wizard.steps.settings.fields.BackendConfig.script.Command.placeholder')"
                      type="text"
                      validation="required"
                      :autocomplete="autoComplete"
                    />
                  </template>
                  <template v-else>
                    <!-- TODO - What is this? Can we delete it? -->
                    <FormKit
                      :classes="{
                        outer: 'max-w-full',
                      }"
                      name="Command"
                      label="Webhook URL"
                      help="This is the endpoint we will send the Knoc info to"
                      placeholder="https://example.com/webhook"
                      type="text"
                      validation="required|isHttpUrl"
                      :validation-rules="{ isHttpUrl }"
                      :validation-messages="{ isHttpUrl: 'URL must be http or https' }"
                      :autocomplete="autoComplete"
                    />
                  </template>
                </FormKit>
              </FormKit>

              <FormKit v-if="type === 'SCRIPT'" name="ACLConfig" type="group">
                <FormKit name="script" type="group">
                  <FormKit
                    :classes="{
                      outer: 'max-w-full',
                    }"
                    name="Name"
                    :label="t('pages.wizard.steps.settings.fields.ACLConfig.fields.script.fields.Name.label')"
                    :placeholder="
                      t('pages.wizard.steps.settings.fields.ACLConfig.fields.script.fields.Name.placeholder')
                    "
                    type="text"
                    :autocomplete="autoComplete"
                  />
                </FormKit>
              </FormKit>

              <FormKit v-if="type === 'PROXY'" name="BackendConfig" type="group">
                <FormKit v-if="proxyType === 'haproxy'" name="haproxy" type="group">
                  <FormKit
                    v-model="protocol"
                    :classes="{
                      outer: 'max-w-full',
                    }"
                    name="Protocol"
                    :label="t('pages.wizard.steps.settings.fields.BackendConfig.proxy.Protocol.label')"
                    :options="
                      ['unix', 'tcp'].map((option) => ({
                        label: t(`pages.wizard.steps.settings.fields.BackendConfig.proxy.Protocol.options.${option}`),
                        value: option,
                      }))
                    "
                    type="select"
                    validation="required"
                  />

                  <FormKit
                    v-model="address"
                    :classes="{
                      outer: 'max-w-full',
                    }"
                    name="Address"
                    :label="t(`pages.wizard.steps.settings.fields.BackendConfig.proxy.Address.${protocol}.label`)"
                    :help="t(`pages.wizard.steps.settings.fields.BackendConfig.proxy.Address.${protocol}.help`)"
                    type="text"
                    validation="required"
                    :autocomplete="autoComplete"
                  />
                </FormKit>

                <FormKit v-if="proxyType === 'knocproxy'" name="knocproxy" type="group">
                  <FormKit
                    v-model="protocol"
                    :classes="{
                      outer: 'max-w-full',
                    }"
                    name="Protocol"
                    :label="t('pages.wizard.steps.settings.fields.BackendConfig.proxy.Protocol.label')"
                    :options="
                      ['unix', 'tcp'].map((option) => ({
                        label: t(`pages.wizard.steps.settings.fields.BackendConfig.proxy.Protocol.options.${option}`),
                        value: option,
                      }))
                    "
                    type="select"
                    validation="required"
                  />

                  <FormKit
                    v-model="address"
                    :classes="{
                      outer: 'max-w-full',
                    }"
                    name="Address"
                    :label="t(`pages.wizard.steps.settings.fields.BackendConfig.proxy.Address.${protocol}.label`)"
                    :help="t(`pages.wizard.steps.settings.fields.BackendConfig.proxy.Address.${protocol}.help`)"
                    type="text"
                    validation="required"
                    :autocomplete="autoComplete"
                  />
                </FormKit>
              </FormKit>

              <template v-if="type === 'LINUX'">
                <FormKit
                  v-model="linuxBackendType"
                  :classes="{
                    outer: 'max-w-full',
                  }"
                  name="LinuxBackendType"
                  :label="t('pages.wizard.steps.settings.fields.LinuxBackendType.label')"
                  :options="
                    ['ipset', 'pf'].map((option) => ({
                      label: t(`pages.wizard.steps.settings.fields.LinuxBackendType.options.${option}`),
                      value: option,
                    }))
                  "
                  type="select"
                  validation="required"
                />

                <FormKit v-if="linuxBackendType === 'ipset'" name="ACLConfig" type="group">
                  <FormKit name="ipset" type="group">
                    <div class="flex">
                      <FormKit
                        :classes="{
                          outer: 'max-w-full',
                        }"
                        :label="t('pages.wizard.steps.settings.fields.ACLConfig.ipset.V4SetName.label')"
                        name="V4SetName"
                        :autocomplete="autoComplete"
                        :help="
                          agentIPv4Sets.length
                            ? t('pages.wizard.steps.settings.fields.ACLConfig.ipset.V4SetName.help', {
                                sets: agentIPv4Sets.join(', '),
                              })
                            : undefined
                        "
                        type="text"
                        validation="require_one:V6SetName"
                      />

                      <Tooltip
                        aria-label="IPv4 Ipset"
                        :focusable="true"
                        :text="t('pages.wizard.steps.settings.fields.ACLConfig.ipset.V4SetName.title')"
                        class="ml-3 mt-8"
                      >
                        <QuestionMarkCircleIcon class="h-6 w-6 text-muted-foreground" />
                      </Tooltip>
                    </div>

                    <div class="flex">
                      <FormKit
                        :classes="{
                          outer: 'max-w-full',
                        }"
                        name="V6SetName"
                        :label="t('pages.wizard.steps.settings.fields.ACLConfig.ipset.V6SetName.label')"
                        :autocomplete="autoComplete"
                        :help="
                          agentIPv6Sets.length
                            ? t('pages.wizard.steps.settings.fields.ACLConfig.ipset.V6SetName.help', {
                                sets: agentIPv6Sets.join(', '),
                              })
                            : undefined
                        "
                        type="text"
                        validation="require_one:V4SetName"
                      />

                      <Tooltip
                        aria-label="IPv6 Ipset"
                        :focusable="true"
                        class="ml-3 mt-8"
                        :text="t('pages.wizard.steps.settings.fields.ACLConfig.ipset.V6SetName.title')"
                      >
                        <QuestionMarkCircleIcon class="h-6 w-6 text-muted-foreground" />
                      </Tooltip>
                    </div>
                  </FormKit>
                </FormKit>

                <FormKit v-if="linuxBackendType === 'pf'" name="ACLConfig" type="group">
                  <FormKit name="pf" type="group">
                    <div class="flex">
                      <FormKit
                        :classes="{
                          outer: 'max-w-full',
                        }"
                        :label="t('pages.wizard.steps.settings.fields.ACLConfig.pf.TableName.label')"
                        name="TableName"
                        :autocomplete="autoComplete"
                        :help="
                          agentPfTables.length
                            ? t('pages.wizard.steps.settings.fields.ACLConfig.pf.TableName.help', {
                                tables: agentPfTables.join(', '),
                              })
                            : undefined
                        "
                        type="text"
                        validation="required"
                      />

                      <Tooltip
                        aria-label="PF Table Name"
                        :focusable="true"
                        :text="t('pages.wizard.steps.settings.fields.ACLConfig.pf.TableName.title')"
                        class="ml-3 mt-8"
                      >
                        <QuestionMarkCircleIcon class="h-6 w-6 text-muted-foreground" />
                      </Tooltip>
                    </div>
                  </FormKit>
                </FormKit>
              </template>

              <FormKit v-if="type === 'WINDOWS'" name="ACLConfig" type="group">
                <FormKit name="windows_firewall" type="group">
                  <div v-if="type === 'WINDOWS'" class="flex">
                    <FormKit
                      :label="t('pages.wizard.steps.settings.fields.ACLConfig.windows_firewall.ruleName.label')"
                      name="RuleName"
                      :classes="{ outer: 'max-w-full' }"
                      :autocomplete="autoComplete"
                      :options="[
                        { label: '&nbsp;', value: '' },
                        ...agentWindowsRules.map((option) => ({
                          value: option.name,
                          label: `${option.name} (${option.ports}/${option.protocol})`,
                        })),
                      ]"
                      type="select"
                      validation="required"
                    />

                    <Tooltip
                      aria-label="Windows firewall rule name"
                      :focusable="true"
                      :text="t('pages.wizard.steps.settings.fields.ACLConfig.windows_firewall.ruleName.title')"
                      class="ml-3 mt-8"
                    >
                      <QuestionMarkCircleIcon class="h-6 w-6 text-muted-foreground" />
                    </Tooltip>
                  </div>
                </FormKit>
              </FormKit>

              <FormKit
                v-model="description"
                :classes="{
                  outer: 'max-w-full',
                }"
                name="Description"
                :label="t('pages.wizard.steps.settings.fields.Description.label')"
                :placeholder="t('pages.wizard.steps.settings.fields.Description.placeholder')"
                type="text"
                validation="required"
                :autocomplete="autoComplete"
              />

              <FormKit
                v-model="url"
                :classes="{
                  outer: 'max-w-full',
                }"
                name="URL"
                :label="t('pages.wizard.steps.settings.fields.URL.label')"
                :placeholder="t('pages.wizard.steps.settings.fields.URL.placeholder')"
                type="text"
                :autocomplete="autoComplete"
              />

              <div class="flex space-x-4">
                <FormKit
                  :classes="{
                    outer: 'basis-0 grow shrink-0',
                  }"
                  name="Category"
                  :label="t('pages.wizard.steps.settings.fields.Category.label')"
                  :placeholder="t('pages.wizard.steps.settings.fields.Category.placeholder')"
                  type="text"
                />

                <FormKit
                  v-model="icon"
                  :classes="{
                    outer: 'basis-0 grow shrink-0',
                    inner:
                      'relative flex items-center bg-white border border-neutral-400 rounded focus-within:ring-1 focus-within:!ring-blue-500 focus-within:!border-blue-500 group-data-[invalid]:border-red-500 group-data-[invalid]:ring-1 group-data-[invalid]:ring-red-500 group-data-[disabled]:bg-neutral-100 group-data-[disabled]:!cursor-not-allowed shadow group-[]/repeater:shadow-none group-[]/multistep:shadow-none group-data-[multiple]:rounded dark:bg-transparent dark:border-neutral-500 dark:group-data-[disabled]:bg-neutral-800/5 dark:group-data-[invalid]:border-red-500 dark:group-data-[invalid]:ring-red-500 formkit-inner',
                  }"
                  name="Icon"
                  :label="t('pages.wizard.steps.settings.fields.Icon.label')"
                  :type="iconSelect"
                />
              </div>
            </div>
          </div>
        </div>

        <div>
          <KnocCardPreview :form="form" :knoc-options-form="knocOptionsForm" />
        </div>
      </div>

      <div class="flex justify-end space-x-4">
        <slot />
      </div>
    </div>
  </FormKit>
</template>
